import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, ChevronDown, Check, Globe } from 'lucide-react';
import AnalysisTopics from './promo/AnalysisTopics';
import ArticlesList from './promo/ArticlesList';
import Strategy from './promo/Strategy';

const TABS = [
  { key: 'topics', labelKey: 'Analysis & Topics' },
  { key: 'articles', labelKey: 'Articles' },
  { key: 'strategy', labelKey: 'Strategy' },
];

const PromoTab = ({ monitorId, monitor, geoLocations = [] }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('topics');
  const [selectedGeoId, setSelectedGeoId] = useState(null); // null = "All" (backward compat)
  const [showGeoDrop, setShowGeoDrop] = useState(false);
  const geoDropRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (geoDropRef.current && !geoDropRef.current.contains(e.target)) {
        setShowGeoDrop(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset selectedGeoId if the selected geo no longer exists in geoLocations
  // (e.g. location deleted or data refreshed while user is on this tab)
  useEffect(() => {
    if (selectedGeoId && !geoLocations.find(g => g.id === selectedGeoId)) {
      setSelectedGeoId(null);
    }
  }, [geoLocations, selectedGeoId]);

  // Derive geoLabel from selectedGeoId
  const selectedGeo = geoLocations.find(g => g.id === selectedGeoId) || null;
  const geoLabel = selectedGeo ? selectedGeo.geo_label : null;
  // Only completed locations have usable data
  const completedGeos = geoLocations.filter(g => g.status === 'completed');

  // Props to pass to children (Phase 1: accepted but not yet used by children)
  const childProps = {
    monitorId,
    monitor,
    ...(selectedGeoId ? { geoId: selectedGeoId, geoLabel } : {}),
  };

  return (
    <div data-testid="promo-tab">
      {/* Location selector — only shown when monitor has geo locations */}
      {completedGeos.length > 0 && (
        <div className="mb-5 flex items-center gap-3" data-testid="promo-geo-selector">
          <div className="relative" ref={geoDropRef}>
            <button
              onClick={() => setShowGeoDrop(!showGeoDrop)}
              className={`flex items-center gap-2 px-4 py-2 border text-sm font-medium transition-colors ${
                selectedGeoId
                  ? 'bg-emerald-50 border-[#1BB594] text-[#1BB594]'
                  : 'bg-white border-[#E2DDD8] text-slate-600 hover:border-[#1BB594]/50'
              }`}
              data-testid="promo-geo-trigger"
            >
              <MapPin className="w-4 h-4" />
              <span>{selectedGeoId && geoLabel ? geoLabel : 'All locations'}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showGeoDrop ? 'rotate-180' : ''}`} />
            </button>
            {showGeoDrop && (
              <div className="absolute z-50 mt-1 w-64 bg-white border border-[#E2DDD8] shadow-lg max-h-60 overflow-y-auto" data-testid="promo-geo-dropdown">
                {/* "All locations" option */}
                <button
                  onClick={() => { setSelectedGeoId(null); setShowGeoDrop(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F5F3EE] flex items-center justify-between ${
                    !selectedGeoId ? 'bg-emerald-50 text-[#1BB594]' : 'text-slate-700'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    <span className="font-medium">All locations</span>
                  </span>
                  {!selectedGeoId && <Check className="w-4 h-4 text-[#1BB594]" />}
                </button>
                {/* Divider */}
                <div className="border-t border-[#E2DDD8]" />
                {/* Completed locations */}
                {completedGeos.map((geo) => (
                  <button
                    key={geo.id}
                    onClick={() => { setSelectedGeoId(geo.id); setShowGeoDrop(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F5F3EE] flex items-center justify-between ${
                      selectedGeoId === geo.id ? 'bg-emerald-50 text-[#1BB594]' : 'text-slate-700'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-[#1BB594]" />
                      <span className="font-medium">{geo.geo_label}</span>
                    </span>
                    {selectedGeoId === geo.id && <Check className="w-4 h-4 text-[#1BB594]" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedGeoId && (
            <span className="text-xs text-slate-400">
              Promotion data filtered for this location
            </span>
          )}
        </div>
      )}

      {/* Sub-navigation */}
      <div className="flex gap-1 mb-6 border-b border-[#E2DDD8]" data-testid="promo-sub-nav">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors border-b-2 ${
              activeTab === tab.key
                ? 'border-[#1BB594] text-[#1BB594]'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
            }`}
            data-testid={`promo-tab-${tab.key}`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>
      {activeTab === 'topics' && <AnalysisTopics {...childProps} />}
      {activeTab === 'articles' && <ArticlesList {...childProps} />}
      {activeTab === 'strategy' && <Strategy {...childProps} />}
    </div>
  );
};
export default PromoTab;
